<script lang="ts" setup>
	import { useProductsApi, type GroupProductByNameContext, type ProductContext } from '~/services/apis/products'

	const { urlQuery, toLower, productCategoryType } = useUtils()
	const { internationalNoFormat } = useCurrency()
	const productCategory = computed(() => toLower(urlQuery.value?.page ?? ''))
	const { getProductList, groupedProductsByCategory } = useProductsApi()
	const loadingProducts = shallowRef(true)
	const productList = ref<
		Record<string, { category: string; list: ProductContext; [id: string]: unknown }> | GroupProductByNameContext
	>({})
	const productType = computed(() => productCategoryType(productCategory.value))
	const iconPack: Record<string, { fill: string; line: string }> = {
		health: {
			line: 'heart-rate',
			fill: 'heart-rate'
		},
		auto: {
			line: 'auto-bus',
			fill: 'auto-bus'
		},
		gadget: {
			line: 'monitor-mobile',
			fill: 'monitor-mobile'
		},
		travel: {
			line: 'plane',
			fill: 'plane'
		}
	}
	const tabsGroup = computed<lineTabContext[]>(() => {
		const keys = Object.keys(productList.value)
		if (Array.isArray(keys)) {
			return keys.map(item => {
				return {
					name: item,
					key: item,
					icon: iconPack[productCategory.value]
				}
			})
		}
		return []
	})
	const selected = computed(() => String(urlQuery.value?.product ?? ''))
	const products = computed(() => {
		if (Object.values(productList).length && selected.value) {
			return (productList.value[selected.value]?.list || []).map(item => {
				const biAnnualPrice = item.prices?.find(p => toLower(p.cover_period) === 'bi-annual')
				const market_price =
					toLower(item.productCategory.name) !== 'health'
						? item.market_price
						: parseInt(String(biAnnualPrice?.market_price)) * Number(biAnnualPrice?.month)
				return {
					...item,
					market_price,
					price: {
						...item.price,
						market_price
					}
				}
			})
		}
		return []
	})
	const selectedProductId = defineModel<string>({ default: '' })
	const defaultKey = shallowRef('zencare')

	onMounted(() => {
		resolve(async () => {
			if (productType.value?.isHealth) {
				// This will only execute for health since health plans are now grouped
				const result = await resolve(() => getProductList({ category: productCategory.value }))
				if (result) {
					productList.value = result
				}
			} else {
				const result = await resolve(groupedProductsByCategory)
				if (result) {
					const key = String(Object.keys(result).find(item => toLower(item) === productCategory.value) || '')
					productList.value = {
						[key]: {
							category: key,
							list: result[key]
						}
					}
					defaultKey.value = key
				}
			}
		}, loadingProducts)
	})
</script>

<template>
	<div class="flexed w-full justify-center">
		<tabs-line-tab
			:active-tab="defaultKey"
			query-key="product"
			use-url-update
			:tabs="tabsGroup"
			:ui="{
				line: {
					borderColor: 'border-main'
				},
				tab: {
					width: 'md:min-w-[200px] sm:min-w-[130px] min-w-[100px]',
					default: 'md:border-b-[2px] border-gray-200 text-sm md:text-base',
					padding: 'py-[25px]',
					wrapper: {
						default: 'w-full max-w-[640px]'
					},
					body: {
						default: 'sm:bg-primary-25 flex  w-full',
						padding: ' md:py-0',
						alignItem: 'items-center justify-evenly md:justify-normal',
						border: 'rounded-t-lg border-b md:border-0',
						gap: 'gap-x-3 md:gap-x-0'
					},
					active: {
						color: 'text-blue-950',
						font: 'font-semibold'
					},
					inactive: {
						color: 'text-gray-500',
						font: 'font-medium'
					}
				}
			}"
			class="w-full max-w-[640px] md:w-auto"
			@selected="selectedProductId = ''"
		>
			<template #tab="{ item }">
				<div class="flex items-center gap-x-[8px]">
					<lazy-tools-icon
						v-if="selected != item?.key"
						dir="line"
						:icon="item.icon.line"
						:width="18"
						:height="18"
					/>
					<lazy-tools-icon v-else dir="fill" :icon="item.icon.fill" :width="18" :height="18" />
					<span class="capitalize">{{ String(item.name).replace('care', 'Care') }}</span>
				</div>
			</template>
		</tabs-line-tab>

		<template v-if="loadingProducts">
			<loader-skeleton :count="1" :height="300" class-list="w-full c-i" />
		</template>
		<shared-carousel v-else :ui="{ wrapper: 'xl:justify-center' }">
			<transition-group
				name="animate"
				enter-active-class="animate__animated animate__fadeIn  animate__faster"
				leave-active-class="animate__animated animate__zoomOut absolute -z-10 animate__faster-x2"
			>
				<template v-for="(item, pi) in products" :key="`price-${pi}`">
					<products-min-benefit-card
						:benefit_highlight="
							item?.pricingBenefits?.dedicated_benfit || item?.pricingBenefits?.benefit_highlight || []
						"
						:name="{
							text: item.name
						}"
						:description="{
							text: internationalNoFormat(item.price?.market_price ?? '0', '₦')
						}"
						:ui="{
							wrapper: {
								height: '',
								hover: '',
								padding: 'px-5 py-5 md:pt-0 pb-5',
								width: 'md:mx-w-[380px] max-w-[330px] w-full',
								bg: [
									selectedProductId != item.id ? 'bg-white' : 'bg-success-25',
									'hover:bg-success-25'
								],
								border: [
									' border hover:border-success-500',
									selectedProductId === item.id ? 'border-success-500' : 'border-gray-300'
								],
								base: `self-stretch flex flex-col  cursor-pointer`,
								transition: ''
							},
							list: { height: '' },
							heading: {
								border: '',
								spacer: 'space-y-1.5',
								name: {
									base: 'inline-block mb-3',
									hover: '',
									size: ''
								},
								description: {
									base: ''
								}
							}
						}"
						class="mx-auto flex-none snap-start scroll-p-4 xl:mx-0"
						@click="selectedProductId = item.id"
					>
						<template #list-icon>
							<lazy-tools-icon
								:dir="item.id === selectedProductId ? 'fill' : 'line'"
								icon="tick-circle"
								circle-class="icon custom-transition"
							/>
						</template>

						<template #name>
							<div
								class="flex items-center gap-x-1 capitalize"
								:class="{
									'font-semibold text-gray-500': selectedProductId != item.id,
									'font-bold text-blue-950': selectedProductId === item.id
								}"
							>
								<a-radio :checked="selectedProductId === item.id" class="rad" />
								{{ String(item.name).replace('care', 'Care') }}
							</div>
						</template>
						<template #description>
							<p class="text-balance text-sm text-gray-500">
								<span class="text-lg font-[700] text-gray-600 sm:text-[1.875em]">
									<span
										v-show="!productType?.isTravel"
										:class="{ 'text-main': selectedProductId === item.id }"
									>
										{{
											productType?.isGadget
												? `${parseFloat(String(item.market_price))}% Gadget Value`
												: item.is_dynamic_price
													? `${parseFloat(String(item.market_price))}% `
													: internationalNoFormat(item.market_price, '₦')
										}}
										{{
											item.code_name === ProductCodeNames.COMPREHENSIVE_AUTO
												? 'Vehicle Value'
												: ''
										}}
									</span>
									<span v-show="productType?.isTravel"> Premium based on: </span>
								</span>
								{{
									productType?.isAuto && item.code_name !== ProductCodeNames.COMPREHENSIVE_AUTO
										? '/ vehicle annually'
										: ''
								}}
								{{ item.code_name === ProductCodeNames.COMPREHENSIVE_AUTO ? '/ vehicle monthly' : '' }}
								{{ productType?.isGadget ? '/ gadget annually' : '' }}
								{{ productType?.isHealth ? '/ person bi-annually' : '' }}
								{{ productType?.isTravel ? 'Duration of travel, Country, Age' : '' }}
							</p>
						</template>
					</products-min-benefit-card>
				</template>
			</transition-group>
		</shared-carousel>
	</div>
</template>

<style scoped>
	.rad :deep(.ant-radio-checked .ant-radio-inner) {
		@apply border-main bg-white after:bg-main;
	}
</style>
